import { StarIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';

interface StarRatingProps {
  rating: number;
  count: number;
  totalStars?: number;
  className?: string;
}

export default function StarRating({
  rating = 0,
  count = 0,
  totalStars = 5,
  className = 'h-5 w-5',
}: StarRatingProps) {
  const { i18n, t } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  return (
    <div className='flex items-center space-x-1 rtl:space-x-reverse'>
      {Array.from({ length: totalStars }, (_, i) => i + 1).map((starIndex) => {
        const fullStars = Math.floor(rating);
        const decimalPart = rating - fullStars;

        let fillPercentage = 0;
        if (starIndex <= fullStars) {
          fillPercentage = 100;
        } else if (starIndex === fullStars + 1) {
          fillPercentage = Math.round(decimalPart * 100);
        }

        const clipPathStyle = isRtl
          ? `inset(0 0 0 ${100 - fillPercentage}%)`
          : `inset(0 ${100 - fillPercentage}% 0 0)`;

        return (
          <div key={starIndex} className='relative ltr:pb-1'>
            <StarIcon
              aria-hidden='true'
              className={`${className} flex-shrink-0 text-gray-300`}
            />
            <div
              className='absolute top-0 left-0 h-full w-full overflow-hidden'
              style={{ clipPath: clipPathStyle }}
            >
              <StarIcon
                aria-hidden='true'
                className={`${className} flex-shrink-0 text-primary`}
              />
            </div>
          </div>
        );
      })}
      <p className='sr-only'>
        {rating}
        {' '}
        out of 5 stars
      </p>
      <p className='ml-2 rtl:mr-2 text-sm text-secondary'>
        {t('productDetails.reviews.reviewsCount', { count })}
      </p>
    </div>
  );
}
